// SPDX-License-Identifier: AGPL-3.0-or-later

import * as PremiumCommands from '@app/features/premium/commands/PremiumCommands';
import * as ShopierCommands from '@app/features/premium/commands/ShopierCommands';
import styles from '@app/features/premium/components/experience/FreeProOffer.module.css';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {GiftIcon} from '@phosphor-icons/react';
import {useEffect, useState} from 'react';

const TITLE = msg({message: 'Get Pro for free', comment: 'Title of the free Pro campaign offer on the Pro page.'});
const BUTTON = msg({message: 'Claim free Pro', comment: 'Button that claims the free Pro campaign.'});
const CLAIMED = msg({message: 'Free Pro is now active. Enjoy!', comment: 'Toast after claiming free Pro.'});
const FAILED = msg({message: 'Free Pro could not be claimed.', comment: 'Toast when claiming free Pro fails.'});

/** Admin-run free Pro campaign; renders nothing unless the current user can claim it. */
export function FreeProOffer() {
	const {i18n} = useLingui();
	const [days, setDays] = useState<number | null>(null);
	const [busy, setBusy] = useState(false);

	useEffect(() => {
		let cancelled = false;
		ShopierCommands.fetchFreeProStatus()
			.then((status) => {
				if (!cancelled && status.available) setDays(status.days);
			})
			.catch(() => {});
		return () => {
			cancelled = true;
		};
	}, []);

	if (days == null) return null;

	const claim = async () => {
		if (busy) return;
		setBusy(true);
		try {
			await ShopierCommands.claimFreePro();
			ToastCommands.success(i18n._(CLAIMED));
			setDays(null);
			void PremiumCommands.refreshPremiumState();
		} catch {
			ToastCommands.error(i18n._(FAILED));
		} finally {
			setBusy(false);
		}
	};

	return (
		<section className={styles.root} data-flx="premium.free-pro-offer">
			<span className={styles.icon} aria-hidden="true">
				<GiftIcon weight="fill" size={22} />
			</span>
			<div className={styles.copy}>
				<h2 className={styles.title}>{i18n._(TITLE)}</h2>
				<p className={styles.subtitle}>{i18n._(msg`Try every Pro feature for ${days} days at no cost. It ends automatically.`)}</p>
			</div>
			<button
				type="button"
				className={styles.button}
				onClick={() => void claim()}
				disabled={busy}
				aria-busy={busy || undefined}
			>
				{i18n._(BUTTON)}
			</button>
		</section>
	);
}
